<script setup lang="ts">
import {computed} from "vue";
import {BadgeCheck} from "@lucide/vue";
import RCSCard from "@/notification-template/components/rcs/RCSCard.vue";
import {RcsCard, RcsSuggestion as IRcsSuggestion} from "@/notification-template/interfaces";
import imagesHelper from "@/core/helpers/imagesHelper";

const props = defineProps<{
    pharmacyName: string;
    /** The first name used to illustrate the personalisation. */
    firstName: string;
    /** Null when the gift carries no video: the button is then omitted. */
    videoLabel: string | null;
}>();

const emit = defineEmits<{
    (e: 'show-qr'): void;
    (e: 'show-video'): void;
}>();

/** The video button is the only URL suggestion; the QR one is a reply. */
const onSuggestionClick = (suggestion: IRcsSuggestion) => {
    if (suggestion.type === 'url') {
        emit('show-video');
    } else {
        emit('show-qr');
    }
};

/**
 * The message is fixed: only the first name and the pharmacy name change.
 * The gift itself is never mentioned — it stays a surprise until the
 * counter scans the QR code.
 */
const payload = computed<RcsCard>(() => ({
    type: 'card',
    title: 'Joyeux anniversaire 🥳',
    content: `Joyeux anniversaire ${props.firstName} 🥳\n`
        + `Toute l'équipe de la ${props.pharmacyName} pense à vous aujourd'hui — on vous a même `
        + 'enregistré une petite vidéo. Et parce que c\'est votre jour, une surprise vous attend : '
        + 'montrez le QR code ci-dessous au comptoir, on s\'occupe du reste.',
    // The same 16:9 illustration the send pipeline attaches to the card
    // (birthday-gift.card_image_url on the HDS, main API public disk).
    media_url: imagesHelper.getURLFromPath('images/birthday/birthday-present.png') ?? '',
    suggestions: [
        ...(props.videoLabel ? [{type: 'url' as const, text: props.videoLabel, url: '#'}] : []),
        {type: 'reply' as const, text: 'Afficher mon QR code'},
    ],
}));
</script>

<template>
  <div class="flex flex-1 flex-col bg-[#f4f7fc]">
    <div class="flex shrink-0 items-center gap-2 border-b border-gray-light bg-white px-3 py-2.5">
      <span class="gradient flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white">
        {{ pharmacyName.slice(0, 2).toUpperCase() }}
      </span>
      <span class="min-w-0 flex-1">
        <span class="flex items-center gap-1 text-xs font-semibold">
          {{ pharmacyName }}
          <BadgeCheck class="h-3.5 w-3.5 text-primary" />
        </span>
        <span class="block text-[10px] text-muted-foreground">Expéditeur vérifié · RCS</span>
      </span>
    </div>

    <div class="flex-1 overflow-y-auto p-3">
      <div class="mb-2 text-center text-[10px] text-muted-foreground">
        Aujourd'hui 9:00
      </div>
      <RCSCard
        :payload="payload"
        @suggestion-click="onSuggestionClick"
      />
    </div>
  </div>
</template>
